import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { useBeforeUnload, useBlocker, useLocation, useNavigate } from "react-router-dom";
import {
  compileBrandingDefinition,
  getCompiledMode,
  safeParseBrandingDefinition,
  type BrandingDefinition,
  type CompiledBrandingArtifact,
} from "@lemn-ltd/brand-contract";
import { BrandStudioPreview } from "@lemn-ltd/brand-studio";
import {
  ActiveFiltersRow,
  Alert,
  Badge,
  Brand,
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  DataTable,
  DockPanel,
  Filter,
  EmptyState,
  Field,
  FormDialog,
  Icon,
  IconButton,
  Input,
  InputSelect,
  ListFiltersBar,
  ListShell,
  Menu,
  MenuItem,
  PageHeader,
  ProgressBar,
  ScreenShell,
  Sidebar,
  Spinner,
  TopBar,
  type DataTableColumn,
  type DockTab,
  type FieldControlProps,
  type SidebarNavGroup,
} from "@lemn-ltd/ui";
import type {
  AuditEventDto,
  BrandingVersionDto,
  ManagedPreviewSessionDto,
  PreviewTargetDto,
  SystemBrandingDto,
  SystemBrandingSummaryDto,
  WorkspaceMcpTokenDto,
  WorkspaceRuntimeCredentialDto,
  WorkspaceDto,
} from "../../api/generated.ts";
import { ControlPlaneApiError, controlPlaneApi } from "../../api/client.ts";
import {
  brandingQueryKeys,
  useAccessContext,
  useBrandingAggregate,
  useBrandingVersions,
  usePreviewSessions,
  usePreviewTargets,
  useOrganizationAudit,
  useSystemBrandings,
  useWorkspaceMcpTokens,
  useWorkspaceRuntimeCredentials,
  useWorkspaceAudit,
  useWorkspaces,
} from "./use-brand-workspace.ts";
import { GovernedBrandStudio } from "./GovernedBrandStudio.tsx";
import {
  closeReservedPreviewWindow,
  navigateReservedPreviewWindow,
  reservePreviewWindow,
} from "./governed-studio-operations.ts";
import {
  canCreateUpdatedPreview,
  canRevokePreviewSession,
  previewSessionTone,
} from "./preview-session-ui.ts";
import {
  createSystemBrandingGalleryRecord,
  filterSystemBrandingGallery,
} from "./system-branding-gallery.ts";
import {
  brandingScreenLabels,
  matchBrandingLocation,
  organizationAuditPath,
  systemBrandingsPath,
  workspacesPath,
  workspaceScreenFromLocation,
  workspaceScreenPath,
  type BrandingScreen,
} from "./branding-navigation.ts";
import {
  filterAuditEvents,
  filterBrandingVersions,
  filterPreviewSessions,
  filterPreviewTargets,
  filterWorkspaceMcpTokens,
  filterWorkspaceRuntimeCredentials,
  filterWorkspaces,
  getWorkspaceMcpTokenDisplayStatus,
  getWorkspaceRuntimeCredentialDisplayStatus,
  toggleControlledFilterValue,
} from "./branding-list-state.ts";

const credentialLifetimeOptions = [
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days", hint: "Recommended" },
  { value: "180", label: "180 days" },
  { value: "365", label: "365 days" },
] as const;

const developmentPublicReadOnlyScreens: readonly BrandingScreen[] = [
  "workspaces",
  "versions",
  "studio",
  "system-brandings",
];

function isDevelopmentPublicReadOnlyScreen(screen: BrandingScreen): boolean {
  return developmentPublicReadOnlyScreens.includes(screen);
}

function fieldControlProps(control: FieldControlProps) {
  return {
    id: control.id,
    ...(control["aria-invalid"] === undefined ? {} : { "aria-invalid": control["aria-invalid"] }),
    ...(control["aria-describedby"] === undefined ? {} : { "aria-describedby": control["aria-describedby"] }),
    ...(control.invalid === undefined ? {} : { invalid: control.invalid }),
    ...(control.disabled === undefined ? {} : { disabled: control.disabled }),
  };
}

export function BrandingWorkspace() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const accessContext = useAccessContext();
  const workspaces = useWorkspaces();
  const readOnlyDemo = accessContext.data?.mode === "development_public_readonly"
    && accessContext.data.readOnly;
  const fullAccess = accessContext.data?.mode === "authenticated"
    && !accessContext.data.readOnly;
  const route = useMemo(() => matchBrandingLocation(location.pathname), [location.pathname]);
  const screen = route?.screen ?? "workspaces";
  const readOnlyRouteBlocked = Boolean(
    readOnlyDemo && route && !isDevelopmentPublicReadOnlyScreen(route.screen),
  );
  const search = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const contextWorkspaceId = route?.workspaceId ?? search.get("workspace") ?? undefined;
  const requestedWorkspace = workspaces.data?.find((item) => item.id === contextWorkspaceId);
  const workspace = requestedWorkspace ?? workspaces.data?.[0];
  const queryWorkspaceId = readOnlyRouteBlocked || screen === "new-workspace" || screen === "workspaces"
    ? undefined
    : workspace?.id;
  const needsVersions = !readOnlyRouteBlocked
    && (screen === "studio" || screen === "versions" || screen === "previews");
  const needsTemplates = !readOnlyRouteBlocked
    && (screen === "studio" || screen === "versions" || screen === "system-brandings");
  const needsTargets = fullAccess && (screen === "studio" || screen === "targets" || screen === "previews");
  const needsSessions = fullAccess && screen === "previews";
  const needsWorkspaceAudit = fullAccess && (screen === "studio" || screen === "workspace-audit");
  const aggregate = useBrandingAggregate(queryWorkspaceId);
  const versions = useBrandingVersions(needsVersions ? queryWorkspaceId : undefined);
  const templates = useSystemBrandings(needsTemplates);
  const targets = usePreviewTargets(needsTargets ? queryWorkspaceId : undefined);
  const previewSessions = usePreviewSessions(needsSessions ? queryWorkspaceId : undefined);
  const audit = useWorkspaceAudit(needsWorkspaceAudit ? queryWorkspaceId : undefined);
  const routeOrganizationId = route?.organizationId;
  const organizationAuditId = fullAccess && screen === "organization-audit" && routeOrganizationId === workspace?.organizationId
    ? routeOrganizationId
    : undefined;
  const organizationAudit = useOrganizationAudit(organizationAuditId);
  const selectedVersionId = search.get("version") ?? undefined;
  const selectedVersion = versions.data?.find((version) => version.id === selectedVersionId)
    ?? preferredVersion(versions.data, aggregate.data?.branding.activeVersionId);
  const editableVersions = versions.data?.filter((version) => version.state === "draft" && version.archivedAt === null) ?? [];
  const previewVersion = editableVersions.find((version) => version.id === selectedVersionId) ?? editableVersions[0];
  const previewDialogRequest = useMemo(() => {
    if (screen !== "previews" || search.get("new") !== "1") return undefined;
    return {
      key: location.search,
      draftId: search.get("version") ?? "",
      targetId: search.get("target") ?? "",
      initialModeId: search.get("mode") ?? "",
    };
  }, [location.search, screen, search]);
  const [notice, setNotice] = useState<{ tone: "success" | "error"; message: string }>();
  const [studioDirty, setStudioDirty] = useState(false);
  const [studioPreview, setStudioPreview] = useState<{
    readonly versionId: string;
    readonly definition: BrandingDefinition;
    readonly modeId: string;
  }>();
  const [studioDraftSource, setStudioDraftSource] = useState<SystemBrandingSummaryDto>();
  const blocker = useBlocker(studioDirty);

  useBeforeUnload((event) => {
    if (!studioDirty) return;
    event.preventDefault();
    event.returnValue = "";
  }, { capture: true });

  useEffect(() => {
    setNotice(undefined);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const page = document.querySelector<HTMLElement>(".branding-main");
    page?.scrollIntoView({ block: "start", behavior: "auto" });
    page?.focus({ preventScroll: true });
  }, [location.pathname]);

  useEffect(() => {
    if (accessContext.isLoading || accessContext.error || workspaces.isLoading || workspaces.error) return;
    const firstWorkspace = workspaces.data?.[0];
    if (!route) {
      navigate("/workspaces", { replace: true });
      return;
    }
    if (readOnlyRouteBlocked) {
      navigate(firstWorkspace ? workspaceScreenPath(firstWorkspace.id, "versions") : workspacesPath(), { replace: true });
      return;
    }
    if (!firstWorkspace && route.screen !== "new-workspace" && route.screen !== "workspaces" && route.screen !== "system-brandings") {
      navigate("/workspaces", { replace: true });
      return;
    }
    if (route.workspaceId && !requestedWorkspace && firstWorkspace) {
      navigate("/workspaces", { replace: true });
      return;
    }
    if (route.screen === "system-brandings" && firstWorkspace && contextWorkspaceId !== workspace?.id) {
      navigate(systemBrandingsPath(workspace?.id ?? firstWorkspace.id), { replace: true });
      return;
    }
    if (route.screen === "organization-audit" && workspace && (
      route.organizationId !== workspace.organizationId || contextWorkspaceId !== workspace.id
    )) {
      navigate(organizationAuditPath(workspace.organizationId, workspace.id), { replace: true });
    }
  }, [accessContext.error, accessContext.isLoading, contextWorkspaceId, navigate, readOnlyRouteBlocked, requestedWorkspace, route, workspace?.id, workspaces.data, workspaces.error, workspaces.isLoading]);

  useEffect(() => {
    if (screen !== "studio" || versions.isLoading || !selectedVersion || !workspace) return;
    if (selectedVersionId === selectedVersion.id) return;
    navigate(`${workspaceScreenPath(workspace.id, "studio")}?version=${encodeURIComponent(selectedVersion.id)}`, { replace: true });
  }, [navigate, screen, selectedVersion, selectedVersionId, versions.isLoading, workspace]);

  useEffect(() => {
    if (screen !== "previews" || versions.isLoading || !previewVersion || !workspace) return;
    if (selectedVersionId === previewVersion.id) return;
    navigate(`${workspaceScreenPath(workspace.id, "previews")}?version=${encodeURIComponent(previewVersion.id)}`, { replace: true });
  }, [navigate, previewVersion, screen, selectedVersionId, versions.isLoading, workspace]);

  const refreshWorkspace = async () => {
    if (!workspace) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: brandingQueryKeys.aggregate(workspace.id) }),
      queryClient.invalidateQueries({ queryKey: brandingQueryKeys.versions(workspace.id) }),
      queryClient.invalidateQueries({ queryKey: brandingQueryKeys.targets(workspace.id) }),
      queryClient.invalidateQueries({ queryKey: brandingQueryKeys.sessions(workspace.id) }),
      queryClient.invalidateQueries({ queryKey: brandingQueryKeys.audit(workspace.id) }),
      queryClient.invalidateQueries({ queryKey: brandingQueryKeys.organizationAudit(workspace.organizationId) }),
    ]);
  };

  const action = useMutation({
    mutationFn: async (run: () => Promise<unknown>) => run(),
    onMutate: () => setNotice(undefined),
    onSuccess: async () => {
      setNotice({ tone: "success", message: "The governed operation completed." });
      await refreshWorkspace();
    },
    onError: (error) => setNotice({ tone: "error", message: errorMessage(error) }),
  });

  if (accessContext.isLoading) return <main className="state"><Spinner aria-label="Loading access context" /></main>;
  if (accessContext.error) return <Failure title="Access context could not load" error={accessContext.error} retry={() => accessContext.refetch()} />;
  if (workspaces.isLoading) return <main className="state"><Spinner aria-label="Loading workspaces" /></main>;
  if (workspaces.error) return <Failure title="Workspaces could not load" error={workspaces.error} retry={() => workspaces.refetch()} />;
  if (readOnlyRouteBlocked) return <main className="state"><Spinner aria-label="Opening read-only workspace" /></main>;
  if (!route || (route.workspaceId && !requestedWorkspace)) return <main className="state"><Spinner aria-label="Opening workspace" /></main>;

  const selectVersion = (versionId: string) => {
    if (!workspace) return;
    navigate(`${workspaceScreenPath(workspace.id, "studio")}?version=${encodeURIComponent(versionId)}`);
  };

  const selectPreviewVersion = (versionId: string) => {
    if (!workspace) return;
    navigate(`${workspaceScreenPath(workspace.id, "previews")}?version=${encodeURIComponent(versionId)}`);
  };

  const navigateTo = (nextScreen: BrandingScreen) => {
    if (nextScreen === "workspaces") {
      navigate(workspacesPath());
      return;
    }
    if (nextScreen === "system-brandings") {
      navigate(systemBrandingsPath(workspace?.id));
      return;
    }
    if (nextScreen === "new-workspace") {
      navigate("/workspaces/new");
      return;
    }
    if (!workspace) {
      navigate(workspacesPath());
      return;
    }
    if (nextScreen === "organization-audit") {
      navigate(organizationAuditPath(workspace.organizationId, workspace.id));
      return;
    }
    navigate(workspaceScreenPath(workspace.id, nextScreen));
  };

  const changeWorkspace = (nextWorkspaceId: string) => {
    const nextWorkspace = workspaces.data?.find((item) => item.id === nextWorkspaceId);
    if (!nextWorkspace) return;
    if (screen === "system-brandings") {
      navigate(systemBrandingsPath(nextWorkspace.id));
    } else if (screen === "organization-audit") {
      navigate(organizationAuditPath(nextWorkspace.organizationId, nextWorkspace.id));
    } else {
      navigate(workspaceScreenPath(nextWorkspace.id, route ? workspaceScreenFromLocation(route) : "versions"));
    }
    setNotice(undefined);
  };

  const sidebarGroups: readonly SidebarNavGroup[] = [
    {
      items: [
        { id: "workspaces", label: "Workspaces", active: screen === "workspaces" || screen === "new-workspace", onSelect: () => navigateTo("workspaces") },
      ],
    },
    ...(workspace ? [
      {
        header: "Workspace branding",
        items: [{
          id: "branding",
          label: workspace.name,
          active: screen === "studio" || screen === "versions",
          defaultExpanded: true,
          children: [
            { id: "versions", label: "Versions", active: screen === "versions", onSelect: () => navigateTo("versions") },
            { id: "studio", label: "Brand Studio", active: screen === "studio", onSelect: () => navigateTo("studio") },
          ],
        }],
      },
      ...(!readOnlyDemo ? [{
        header: "Delivery",
        items: [{
          id: "delivery",
          label: "Previews",
          active: screen === "targets" || screen === "previews",
          defaultExpanded: true,
          children: [
            { id: "targets", label: "Preview targets", active: screen === "targets", onSelect: () => navigateTo("targets") },
            { id: "previews", label: "Preview sessions", active: screen === "previews", onSelect: () => navigateTo("previews") },
          ],
        }],
      }, {
        header: "Access",
        items: [{
          id: "access",
          label: "Credentials",
          active: screen === "agent-access" || screen === "runtime-access",
          children: [
            { id: "agent-access", label: "Agent MCP", active: screen === "agent-access", onSelect: () => navigateTo("agent-access") },
            { id: "runtime-access", label: "Runtime credentials", active: screen === "runtime-access", onSelect: () => navigateTo("runtime-access") },
          ],
        }],
      }, {
        header: "Governance",
        items: [
          { id: "workspace-audit", label: "Workspace audit", active: screen === "workspace-audit", onSelect: () => navigateTo("workspace-audit") },
        ],
      }] : []),
    ] : []),
    {
      header: "Global",
      items: [
        { id: "system-brandings", label: "System branding library", active: screen === "system-brandings", onSelect: () => navigateTo("system-brandings") },
        ...(!readOnlyDemo && workspace ? [{ id: "organization-audit", label: "Organization audit", active: screen === "organization-audit", onSelect: () => navigateTo("organization-audit") }] : []),
      ],
    },
  ];

  const workspaceSwitcher = workspace && screen !== "workspaces" && screen !== "new-workspace" ? (
    <div className="workspace-switcher">
      <span>Workspace</span>
      <InputSelect
        aria-label="Workspace"
        options={(workspaces.data ?? []).map((item) => ({ value: item.id, label: item.name, hint: item.slug }))}
        value={workspace.id}
        onValueChange={changeWorkspace}
      />
      {!readOnlyDemo ? <Button variant="secondary" onClick={() => navigate("/workspaces/new")}>New workspace</Button> : null}
    </div>
  ) : null;

  const pageLabel = brandingScreenLabels[screen];
  const breadcrumbs = route?.workspaceId && workspace ? [
    { label: "Workspaces", onClick: () => navigateTo("workspaces") },
    { label: workspace.name, onClick: () => navigateTo("versions") },
    { label: pageLabel },
  ] : screen === "new-workspace" ? [
    { label: "Workspaces", onClick: () => navigateTo("workspaces") },
    { label: pageLabel },
  ] : [{ label: pageLabel }];

  const renderStudio = () => (
    <>
      <PageHeader
        title={selectedVersion?.title ?? "Brand Studio"}
        subtitle="Configure the selected branding version step by step. Published and archived versions open read-only."
        actions={<div className="card-actions">
          {!readOnlyDemo ? <DraftDialog
            templates={templates.data ?? []}
            trigger={<Button>New draft</Button>}
            onCreate={(template, title) => controlPlaneApi.createVersion(workspace?.id ?? "", {
              title,
              source: { type: "system_branding", templateId: template.id, templateVersion: template.version },
              idempotencyKey: crypto.randomUUID(),
            })}
            onCreated={async (result) => {
              await refreshWorkspace();
              const id = createdVersionId(result);
              if (id) selectVersion(id);
              setNotice({ tone: "success", message: "The branding draft was created." });
            }}
          /> : null}
          <Button variant="secondary" onClick={() => navigateTo("versions")}>Back to versions</Button>
        </div>}
      />
      {!readOnlyDemo && studioDraftSource ? (
        <DraftDialog
          key={`${studioDraftSource.id}@${studioDraftSource.version}`}
          templates={templates.data ?? []}
          initialOpen
          initialTemplate={studioDraftSource}
          initialTitle={`${studioDraftSource.name} variation`}
          onClose={() => setStudioDraftSource(undefined)}
          onCreate={(template, title) => controlPlaneApi.createVersion(workspace?.id ?? "", {
            title,
            source: { type: "system_branding", templateId: template.id, templateVersion: template.version },
            idempotencyKey: crypto.randomUUID(),
          })}
          onCreated={async (result) => {
            await refreshWorkspace();
            const id = createdVersionId(result);
            if (id) selectVersion(id);
            setNotice({ tone: "success", message: "The branding draft was created." });
          }}
        />
      ) : null}
      {aggregate.error || versions.error || templates.error || targets.error ? (
        <Alert variant="error" title="Brand Studio could not load" message={errorMessage(aggregate.error ?? versions.error ?? templates.error ?? targets.error)} />
      ) : aggregate.isLoading || versions.isLoading || templates.isLoading || targets.isLoading ? (
        <div className="state-panel"><Spinner aria-label="Loading Brand Studio" /></div>
      ) : (
        <div className="studio-workspace">
          <section className="editor-panel">
            {selectedVersion && workspace ? (
              <>
                {selectedVersion.lastPublicationErrorCode ? <Alert variant="error" title="Publication returned this draft for correction" message={`Safe error: ${selectedVersion.lastPublicationErrorCode}. Review, validate, and publish again.`} /> : null}
                <GovernedBrandStudio
                  key={selectedVersion.id}
                  workspaceId={workspace.id}
                  version={selectedVersion}
                  readOnly={readOnlyDemo}
                  previewTargets={targets.data ?? []}
                  onRefresh={refreshWorkspace}
                  onNotice={setNotice}
                  onDirtyChange={setStudioDirty}
                  onPreviewChange={setStudioPreview}
                  onRequestSystemBrandingDraft={({ templateId, templateVersion }) => {
                    const template = templates.data?.find((item) => item.id === templateId && item.version === templateVersion);
                    if (!template) {
                      setNotice({ tone: "error", message: "The selected System branding is no longer available." });
                      return;
                    }
                    setStudioDraftSource(template);
                  }}
                  onRequestPreview={({ brandingVersionId, targetId, initialModeId }) => {
                    const params = new URLSearchParams({
                      version: brandingVersionId,
                      new: "1",
                      target: targetId,
                    });
                    if (initialModeId) params.set("mode", initialModeId);
                    navigate(`${workspaceScreenPath(workspace.id, "previews")}?${params.toString()}`);
                  }}
                />
                {!readOnlyDemo ? <PublishedVersionActions
                  workspaceId={workspace.id}
                  version={selectedVersion}
                  activeVersionId={aggregate.data?.branding.activeVersionId ?? null}
                  activeVersion={aggregate.data?.activeVersion ?? null}
                  publisher={audit.data?.find((event) => event.action === "branding.draft.publish" && event.decision === "completed" && event.resourceId === selectedVersion.id)?.actorId ?? null}
                  busy={action.isPending}
                  onDraftCreated={async (result) => {
                    await refreshWorkspace();
                    const id = createdVersionId(result);
                    if (id) selectVersion(id);
                    setNotice({ tone: "success", message: "A new draft was created from the published version." });
                  }}
                  run={(operation, onSuccess) => {
                    if (onSuccess) action.mutate(operation, { onSuccess });
                    else action.mutate(operation);
                  }}
                /> : null}
              </>
            ) : <EmptyState intent="first-run" title="Create the first branding draft" description="Choose a pinned system branding to begin." action={<Button onClick={() => navigateTo("system-brandings")}>Browse system brandings</Button>} />}
          </section>
        </div>
      )}
    </>
  );

  const renderVersions = () => {
    return (
      <BrandingVersionsPanel
        readOnly={readOnlyDemo}
        error={templates.error ?? versions.error}
        loading={templates.isLoading || versions.isLoading}
        templates={templates.data ?? []}
        versions={versions.data ?? []}
        onOpenVersion={selectVersion}
        onRetry={() => { void Promise.all([templates.refetch(), versions.refetch()]); }}
        onCreate={(template, title) => controlPlaneApi.createVersion(workspace?.id ?? "", {
          title,
          source: { type: "system_branding", templateId: template.id, templateVersion: template.version },
          idempotencyKey: crypto.randomUUID(),
        })}
        onCreated={async (result) => {
          await refreshWorkspace();
          const id = createdVersionId(result);
          if (id) selectVersion(id);
          setNotice({ tone: "success", message: "The branding draft was created." });
        }}
      />
    );
  };

  const renderCurrentScreen = () => {
    if (screen === "workspaces" || screen === "new-workspace") {
      return (
        <WorkspacesPanel
          readOnly={readOnlyDemo}
          initialDialogOpen={screen === "new-workspace"}
          workspaces={workspaces.data ?? []}
          onCancelCreate={() => navigate(workspacesPath(), { replace: true })}
          onOpenWorkspace={(id) => navigate(workspaceScreenPath(id, "versions"))}
          onCreated={async (id) => {
            await queryClient.invalidateQueries({ queryKey: brandingQueryKeys.workspaces });
            navigate(workspaceScreenPath(id, "versions"), { replace: true });
          }}
        />
      );
    }
    if (screen === "system-brandings") {
      return <><PageHeader title="System branding library" subtitle="Global, pinned starting points. Select one when creating a workspace branding draft." />{!workspace ? <Alert variant="info" title="Previewing the global library" message="Create or select a workspace before using a System branding as a new draft." /> : null}<SystemBrandingsPanel readOnly={readOnlyDemo} templates={templates.data ?? []} loading={templates.isLoading} error={templates.error} retry={() => templates.refetch()} workspaceId={workspace?.id} onCreated={async (result) => { if (!workspace) return; await refreshWorkspace(); const id = createdVersionId(result); if (id) selectVersion(id); setNotice({ tone: "success", message: "The System branding was opened as a new draft." }); }} /></>;
    }
    if (!workspace) return <EmptyState intent="first-run" title={readOnlyDemo ? "Demo workspace unavailable" : "Create a workspace"} description={readOnlyDemo ? "The synthetic Lunaria Public Demo workspace has not been provisioned." : "A workspace owns one branding aggregate and its immutable versions."} action={!readOnlyDemo ? <Button onClick={() => navigate("/workspaces/new")}>New workspace</Button> : undefined} />;
    switch (screen) {
      case "studio": return renderStudio();
      case "versions": return renderVersions();
      case "targets": return <PreviewTargetsManager key={workspace.id} workspaceId={workspace.id} targets={targets.data ?? []} loading={targets.isLoading} error={targets.error} retry={() => targets.refetch()} onSaved={refreshWorkspace} />;
      case "previews": return versions.error || targets.error ? <Alert variant="error" title="Preview dependencies could not load" message={errorMessage(versions.error ?? targets.error)} /> : versions.isLoading || targets.isLoading ? <div className="state-panel"><Spinner aria-label="Loading preview dependencies" /></div> : <PreviewSessionsPanel key={workspace.id} workspaceId={workspace.id} sessions={previewSessions.data?.pages.flatMap((page) => page.items) ?? []} editableVersions={editableVersions} selectedVersion={previewVersion} onSelectVersion={selectPreviewVersion} targets={targets.data ?? []} loading={previewSessions.isLoading} error={previewSessions.error} retry={() => previewSessions.refetch()} hasMore={previewSessions.hasNextPage} loadingMore={previewSessions.isFetchingNextPage} loadMore={() => previewSessions.fetchNextPage()} onChanged={refreshWorkspace} onNotice={setNotice} {...(previewDialogRequest ? { initialCreateRequest: previewDialogRequest } : {})} onInitialCreateRequestConsumed={() => navigate(`${workspaceScreenPath(workspace.id, "previews")}?version=${encodeURIComponent(previewDialogRequest?.draftId || previewVersion?.id || "")}`, { replace: true })} />;
      case "agent-access": return <AgentAccessPanel key={workspace.id} workspaceId={workspace.id} />;
      case "runtime-access": return <RuntimeAccessPanel key={workspace.id} workspaceId={workspace.id} />;
      case "workspace-audit": return <><PageHeader title="Workspace audit" subtitle="Append-only evidence for human, agent, preview, publication, and activation decisions." /><WorkspaceAuditPanel events={audit.data ?? []} loading={audit.isLoading} error={audit.error} retry={() => audit.refetch()} /></>;
      case "organization-audit": return <><PageHeader title="Organization audit" subtitle="Cross-workspace evidence for branding administration." /><WorkspaceAuditPanel events={organizationAudit.data ?? []} loading={organizationAudit.isLoading} error={organizationAudit.error} retry={() => organizationAudit.refetch()} organization /></>;
    }
  };

  const pageContent = (
    <ListShell className="branding-page">
      {readOnlyDemo ? <Alert variant="info" title="Development read-only demo" message="Explore Lunaria Public Demo, its versions, the Brand Studio, live preview, contract, and System Branding Library. All writes and sensitive administration remain disabled at the server boundary." /> : null}
      {notice ? <div className="notice"><Alert variant={notice.tone === "error" ? "error" : "success"} title={notice.tone === "error" ? "Operation failed" : "Operation complete"} message={notice.message} /></div> : null}
      {renderCurrentScreen()}
    </ListShell>
  );
  const topBarStatus = !workspace || screen === "new-workspace" || screen === "workspaces" ? undefined
    : aggregate.isLoading ? <Spinner aria-label="Loading active branding status" />
      : aggregate.error ? <Badge tone="warn">Status unavailable</Badge>
        : aggregate.data?.activeVersion
          ? <Badge tone="success">Active · v{aggregate.data.activeVersion.version ?? "—"}</Badge>
          : <Badge tone="neutral">No active version</Badge>;
  const topBarActions = readOnlyDemo ? (
    <div className="card-actions">
      <Badge tone="info">Read-only demo</Badge>
      {topBarStatus}
    </div>
  ) : topBarStatus;
  const activeStudioPreview = studioPreview?.versionId === selectedVersion?.id
    ? studioPreview
    : undefined;
  const studioLoadError = aggregate.error ?? versions.error ?? templates.error ?? targets.error;
  const studioLoading = aggregate.isLoading || versions.isLoading || templates.isLoading || targets.isLoading;
  const selectedStudioDefinition = selectedVersion
    ? safeParseBrandingDefinition(selectedVersion.definition)
    : undefined;
  const retryStudio = () => {
    void Promise.all([
      aggregate.refetch(),
      versions.refetch(),
      templates.refetch(),
      targets.refetch(),
    ]);
  };
  const studioDockFallback: ReactNode = studioLoadError ? (
    <div className="state-panel">
      <Alert
        variant="error"
        title="Studio preview could not load"
        message={errorMessage(studioLoadError)}
      />
      <Button variant="secondary" onClick={retryStudio}>Retry</Button>
    </div>
  ) : studioLoading ? (
    <div className="state-panel"><Spinner aria-label="Preparing Brand Studio workspace" /></div>
  ) : !selectedVersion ? (
    <EmptyState
      intent="first-run"
      title="No branding version selected"
      description="Create a draft from a pinned System branding before opening the Studio workspace."
      action={<Button onClick={() => navigateTo("system-brandings")}>Browse system brandings</Button>}
    />
  ) : selectedStudioDefinition && !selectedStudioDefinition.success ? (
    <Alert
      variant="error"
      title="Studio preview is incompatible"
      message={selectedStudioDefinition.error.issues
        .slice(0, 5)
        .map((issue) => `${issue.path.join(".") || "$"}: ${issue.message}`)
        .join("; ")}
    />
  ) : (
    <div className="state-panel"><Spinner aria-label="Preparing live branding preview" /></div>
  );
  const studioDockTabs: readonly DockTab[] = [
    {
      id: "preview",
      label: "Preview",
      icon: "monitor",
      content: activeStudioPreview
        ? <BrandStudioPreview value={activeStudioPreview.definition} modeId={activeStudioPreview.modeId} />
        : studioDockFallback,
    },
    {
      id: "contract",
      label: "Contract",
      icon: "file-text",
      content: activeStudioPreview ? (
        <section className="studio-contract" aria-label="BrandingDefinition contract">
          <p>The exact provider-neutral contract currently rendered by the live preview.</p>
          <pre><code>{JSON.stringify(activeStudioPreview.definition, null, 2)}</code></pre>
        </section>
      ) : studioDockFallback,
    },
  ];
  const studioDockPanel = screen === "studio"
    ? <DockPanel tabs={studioDockTabs} />
    : undefined;

  return (
    <ScreenShell
      defaultSidebarMode="expanded"
      defaultDockMode="partial"
      dockStorageKey="agentops-brand-studio"
      collapseBehavior="expand-hide"
      rightPanel={studioDockPanel}
      sidebar={<Sidebar brand={<button className="branding-brand" type="button" onClick={() => navigateTo("workspaces")}><Brand name="AgentOps Branding" initials="AB" /></button>} orgSwitcher={workspaceSwitcher} groups={sidebarGroups} defaultExpandedIds={["branding", "delivery"]} railExpand="flyout" versionTag={<Badge tone="info">vNext</Badge>} />}
      topBar={<TopBar breadcrumb={<Breadcrumb items={breadcrumbs} />} actions={topBarActions} />}
    >
      {screen === "studio" ? <div className="branding-main" role="region" aria-label={pageLabel} tabIndex={-1}>{pageContent}</div> : <main className="branding-main" aria-label={pageLabel} tabIndex={-1}>{pageContent}</main>}
      {blocker.state === "blocked" ? (
        <ConfirmDialog
          open
          title="Discard unsaved branding changes?"
          description="The current Brand Studio draft has local changes that have not been saved."
          confirmLabel="Discard and continue"
          cancelLabel="Keep editing"
          variant="danger"
          onConfirm={() => blocker.proceed()}
          onOpenChange={(open) => { if (!open) blocker.reset(); }}
        />
      ) : null}
    </ScreenShell>
  );
}

function WorkspacesPanel({
  workspaces,
  readOnly,
  initialDialogOpen,
  onCancelCreate,
  onCreated,
  onOpenWorkspace,
}: {
  readonly workspaces: readonly WorkspaceDto[];
  readonly readOnly: boolean;
  readonly initialDialogOpen: boolean;
  readonly onCancelCreate: () => void;
  readonly onCreated: (workspaceId: string) => void | Promise<void>;
  readonly onOpenWorkspace: (workspaceId: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(initialDialogOpen && !readOnly);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [workspaceId, setWorkspaceId] = useState("");
  const [attempted, setAttempted] = useState(false);
  const mutation = useMutation({
    mutationFn: () => controlPlaneApi.createWorkspace({
      name: name.trim(),
      slug: slug.trim(),
      ...(workspaceId.trim() ? { workspaceId: workspaceId.trim() } : {}),
      idempotencyKey: crypto.randomUUID(),
    }),
    onSuccess: (created) => onCreated(created.id),
  });
  const reset = () => {
    setName("");
    setSlug("");
    setSlugTouched(false);
    setWorkspaceId("");
    setAttempted(false);
    mutation.reset();
  };
  const close = () => {
    if (mutation.isPending) return;
    setDialogOpen(false);
    reset();
    onCancelCreate();
  };
  const validSlug = slug.trim().length >= 2
    && slug.trim().length <= 80
    && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug.trim());
  const valid = name.trim().length >= 2 && name.trim().length <= 120 && validSlug;
  const submit = () => {
    setAttempted(true);
    if (!valid || mutation.isPending) return;
    mutation.mutate();
  };
  const rows = filterWorkspaces(workspaces, { query });
  const columns: readonly DataTableColumn<WorkspaceDto>[] = [
    { key: "name", header: "Workspace", sortable: true, sortValue: (item) => item.name, render: (item) => <div className="table-primary"><strong>{item.name}</strong><code>{item.id}</code></div> },
    { key: "slug", header: "Slug", sortable: true, sortValue: (item) => item.slug, render: (item) => <code>{item.slug}</code> },
    { key: "organization", header: "Organization", sortable: true, sortValue: (item) => item.organizationId, render: (item) => <code>{item.organizationId}</code> },
    { key: "updated", header: "Updated", sortable: true, sortValue: (item) => item.updatedAt, render: (item) => formatDateTime(item.updatedAt) },
  ];
  return (
    <>
      <PageHeader
        title="Workspaces"
        subtitle="Choose a project boundary to manage its branding versions, or register a new workspace."
        actions={!readOnly ? (
          <FormDialog
            title="New workspace"
            description="A workspace is the project boundary that owns one branding aggregate and its immutable versions."
            trigger={<Button>New workspace</Button>}
            open={dialogOpen}
            onOpenChange={(open) => { if (open) setDialogOpen(true); else close(); }}
            onCancel={close}
            onSubmit={submit}
            submitLabel="Create workspace"
            submitDisabled={!valid}
            submitting={mutation.isPending}
            error={mutation.error ? errorMessage(mutation.error) : undefined}
          >
            <Field label="Name" required error={attempted && name.trim().length < 2 ? "Enter at least 2 characters." : undefined} state={attempted && name.trim().length < 2 ? "invalid" : "default"}>{(control) => <Input {...fieldControlProps(control)} autoFocus value={name} minLength={2} maxLength={120} required onChange={(event) => { const next = event.target.value; setName(next); if (!slugTouched) setSlug(toWorkspaceSlug(next)); }} />}</Field>
            <Field label="Slug" required hint="Lowercase letters, numbers, and single hyphens." error={attempted && !validSlug ? "Use 2–80 lowercase letters, numbers, or single hyphens." : undefined} state={attempted && !validSlug ? "invalid" : "default"}>{(control) => <Input {...fieldControlProps(control)} value={slug} minLength={2} maxLength={80} pattern="[a-z0-9]+(?:-[a-z0-9]+)*" required onChange={(event) => { setSlugTouched(true); setSlug(event.target.value); }} />}</Field>
            <Field label="Existing consumer Workspace ID" hint="Optional. Register an already pinned consumer identity.">{(control) => <Input {...fieldControlProps(control)} onChange={(event) => setWorkspaceId(event.target.value)} placeholder="Leave empty to generate one" value={workspaceId} />}</Field>
          </FormDialog>
        ) : undefined}
      />
      <ListFiltersBar
        search={query}
        onSearchChange={setQuery}
        searchLabel="Search workspaces"
        searchPlaceholder="Search by name, slug, or ID"
      />
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(item) => item.id}
        onRowClick={(item) => onOpenWorkspace(item.id)}
        onClearFilters={() => setQuery("")}
      />
    </>
  );
}

function BrandingVersionsPanel({
  versions,
  readOnly,
  templates,
  loading,
  error,
  onRetry,
  onOpenVersion,
  onCreate,
  onCreated,
}: {
  readonly versions: readonly BrandingVersionDto[];
  readonly readOnly: boolean;
  readonly templates: readonly SystemBrandingSummaryDto[];
  readonly loading: boolean;
  readonly error: Error | null;
  readonly onRetry: () => void;
  readonly onOpenVersion: (versionId: string) => void;
  readonly onCreate: (template: SystemBrandingSummaryDto, title: string) => Promise<unknown>;
  readonly onCreated: (result: unknown) => void | Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const [states, setStates] = useState<readonly ("draft" | "publishing" | "published" | "archived")[]>([]);
  const [sources, setSources] = useState<readonly BrandingVersionDto["sourceType"][]>([]);
  const rows = filterBrandingVersions(versions, { query, states, sources });
  const columns: readonly DataTableColumn<BrandingVersionDto>[] = [
    { key: "title", header: "Version", sortable: true, sortValue: (version) => version.title, render: (version) => <div className="table-primary"><strong>{version.title}</strong><code>{version.definitionHash.slice(0, 12)}</code></div> },
    { key: "state", header: "State", sortable: true, sortValue: (version) => version.archivedAt ? "archived" : version.state, render: (version) => <StateBadge version={version} /> },
    { key: "number", header: "Release", sortable: true, sortValue: (version) => version.version ?? 0, render: (version) => version.version ? `v${version.version}` : "Draft" },
    { key: "source", header: "Source", sortable: true, sortValue: (version) => version.sourceType, render: (version) => humanizeIdentifier(version.sourceType) },
    { key: "updated", header: "Updated", sortable: true, sortValue: (version) => version.updatedAt, render: (version) => formatDateTime(version.updatedAt) },
  ];
  const activeFilters = [
    ...(states.length ? [{ label: `State: ${states.map(humanizeIdentifier).join(", ")}`, onRemove: () => setStates([]) }] : []),
    ...(sources.length ? [{ label: `Source: ${sources.map(humanizeIdentifier).join(", ")}`, onRemove: () => setSources([]) }] : []),
  ];
  const clear = () => { setQuery(""); setStates([]); setSources([]); };
  return (
    <>
      <PageHeader
        title="Branding versions"
        subtitle="Select a version to inspect it in Brand Studio. Only active, non-archived drafts are editable."
        actions={!readOnly ? <DraftDialog templates={templates} trigger={<Button>New draft</Button>} onCreate={onCreate} onCreated={onCreated} /> : undefined}
      />
      {error ? <div className="state-panel"><Alert variant="error" title="Versions could not load" message={errorMessage(error)} /><Button variant="secondary" onClick={onRetry}>Retry</Button></div> : loading ? <div className="state-panel"><Spinner aria-label="Loading branding versions" /></div> : (
        <>
          <ListFiltersBar
            search={query}
            onSearchChange={setQuery}
            searchLabel="Search branding versions"
            searchPlaceholder="Search title, hash, or source"
            pills={<>
              <Filter label="State" select="multi" selected={states} onSelect={(value) => setStates(toggleControlledFilterValue(states, value as typeof states[number], "multiple"))} options={[{ value: "draft", label: "Draft" }, { value: "publishing", label: "Publishing" }, { value: "published", label: "Published" }, { value: "archived", label: "Archived" }]} />
              <Filter label="Source" select="multi" selected={sources} onSelect={(value) => setSources(toggleControlledFilterValue(sources, value as BrandingVersionDto["sourceType"], "multiple"))} options={[{ value: "blank", label: "Blank" }, { value: "branding_version", label: "Branding version" }, { value: "system_branding", label: "System branding" }]} />
            </>}
          />
          {activeFilters.length ? <ActiveFiltersRow filters={activeFilters} onClearAll={clear} /> : null}
          <DataTable columns={columns} rows={rows} rowKey={(version) => version.id} onRowClick={(version) => onOpenVersion(version.id)} onClearFilters={clear} />
        </>
      )}
    </>
  );
}

function DraftDialog({ templates, trigger, initialOpen = false, initialTemplate, initialTitle, onClose, onCreate, onCreated }: {
  readonly templates: readonly SystemBrandingSummaryDto[];
  readonly trigger?: ReactNode;
  readonly initialOpen?: boolean;
  readonly initialTemplate?: SystemBrandingSummaryDto;
  readonly initialTitle?: string;
  readonly onClose?: () => void;
  readonly onCreate: (template: SystemBrandingSummaryDto, title: string) => Promise<unknown>;
  readonly onCreated: (result: unknown) => void | Promise<void>;
}) {
  const defaultSelection = initialTemplate ? `${initialTemplate.id}@${initialTemplate.version}` : templates[0] ? `${templates[0].id}@${templates[0].version}` : "";
  const [open, setOpen] = useState(initialOpen);
  const [selection, setSelection] = useState(defaultSelection);
  const [title, setTitle] = useState(initialTitle ?? "");
  const [attempted, setAttempted] = useState(false);
  useEffect(() => { if (!selection && templates[0]) setSelection(`${templates[0].id}@${templates[0].version}`); }, [selection, templates]);
  const template = templates.find((item) => `${item.id}@${item.version}` === selection) ?? initialTemplate;
  const resolvedTitle = title.trim() || (template ? `${template.name} variation` : "");
  const mutation = useMutation({
    mutationFn: () => {
      if (!template) throw new Error("Select an available System branding.");
      return onCreate(template, resolvedTitle);
    },
    onSuccess: async (result) => {
      setOpen(false);
      setTitle(initialTitle ?? "");
      setAttempted(false);
      onClose?.();
      await onCreated(result);
    },
  });
  const close = () => {
    if (mutation.isPending) return;
    setOpen(false);
    setTitle(initialTitle ?? "");
    setSelection(defaultSelection);
    setAttempted(false);
    mutation.reset();
    onClose?.();
  };
  const valid = Boolean(template) && resolvedTitle.length >= 2 && resolvedTitle.length <= 120;
  return (
    <FormDialog
      title="New branding draft"
      description="Create an editable version from one exact, pinned System branding."
      trigger={trigger}
      open={open}
      onOpenChange={(next) => { if (next) setOpen(true); else close(); }}
      onCancel={close}
      onSubmit={() => { setAttempted(true); if (valid && !mutation.isPending) mutation.mutate(); }}
      submitLabel="Create draft"
      submitDisabled={!valid}
      submitting={mutation.isPending}
      error={mutation.error ? errorMessage(mutation.error) : undefined}
    >
      <Field label="Draft title" hint="Leave blank to use the System branding name." error={attempted && !valid ? "Enter a title between 2 and 120 characters." : undefined} state={attempted && !valid ? "invalid" : "default"}>{(control) => <Input {...fieldControlProps(control)} autoFocus maxLength={120} minLength={2} value={title} placeholder={template ? `${template.name} variation` : "Draft title"} onChange={(event) => setTitle(event.target.value)} />}</Field>
      <Field label="System branding" required hint="The source ID and version are persisted as immutable provenance.">{(control) => <InputSelect {...fieldControlProps(control)} disabled={Boolean(initialTemplate)} value={selection} onValueChange={setSelection} options={templates.map((item) => ({ value: `${item.id}@${item.version}`, label: item.name, hint: `${item.category} · v${item.version}` }))} />}</Field>
    </FormDialog>
  );
}

function VersionDraftDialog({ version, trigger, onCreate, onCreated }: {
  readonly version: BrandingVersionDto;
  readonly trigger: ReactNode;
  readonly onCreate: (title: string) => Promise<unknown>;
  readonly onCreated: (result: unknown) => void | Promise<void>;
}) {
  const defaultTitle = `${version.title.slice(0, 108)} variation`;
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(defaultTitle);
  const [attempted, setAttempted] = useState(false);
  const mutation = useMutation({
    mutationFn: () => onCreate(title.trim()),
    onSuccess: async (result) => { await onCreated(result); setOpen(false); setTitle(defaultTitle); setAttempted(false); },
  });
  const valid = title.trim().length >= 2 && title.trim().length <= 120;
  const close = () => { if (mutation.isPending) return; setOpen(false); setTitle(defaultTitle); setAttempted(false); mutation.reset(); };
  return (
    <FormDialog title="Create draft from version" description={`Use ${version.title} as immutable source provenance for a new editable draft.`} trigger={trigger} open={open} onOpenChange={(next) => { if (next) setOpen(true); else close(); }} onCancel={close} onSubmit={() => { setAttempted(true); if (valid && !mutation.isPending) mutation.mutate(); }} submitLabel="Create draft" submitDisabled={!valid} submitting={mutation.isPending} error={mutation.error ? errorMessage(mutation.error) : undefined}>
      <Field label="Draft title" required error={attempted && !valid ? "Enter a title between 2 and 120 characters." : undefined} state={attempted && !valid ? "invalid" : "default"}>{(control) => <Input {...fieldControlProps(control)} autoFocus maxLength={120} minLength={2} required value={title} onChange={(event) => setTitle(event.target.value)} />}</Field>
      <Field label="Source version">{(control) => <Input {...fieldControlProps(control)} readOnly value={`${version.title}${version.version ? ` · v${version.version}` : ""} · ${version.definitionHash.slice(0, 12)}`} />}</Field>
    </FormDialog>
  );
}

function AgentAccessPanel({ workspaceId }: { readonly workspaceId: string }) {
  const queryClient = useQueryClient();
  const tokens = useWorkspaceMcpTokens(workspaceId);
  const [query, setQuery] = useState("");
  const [statuses, setStatuses] = useState<readonly ("active" | "overlap" | "expired" | "revoked")[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [expiryDays, setExpiryDays] = useState(90);
  const [attempted, setAttempted] = useState(false);
  const [rotating, setRotating] = useState<WorkspaceMcpTokenDto>();
  const [overlapMinutes, setOverlapMinutes] = useState(5);
  const [pendingRevoke, setPendingRevoke] = useState<WorkspaceMcpTokenDto>();
  const [issued, setIssued] = useState<{ credential: string; token: WorkspaceMcpTokenDto }>();
  const [copyStatus, setCopyStatus] = useState<"copied" | "failed">();
  const refresh = () => queryClient.invalidateQueries({ queryKey: brandingQueryKeys.mcpTokens(workspaceId) });
  const create = useMutation({
    mutationFn: () => controlPlaneApi.createWorkspaceMcpToken(workspaceId, { name: name.trim(), expiryDays, idempotencyKey: crypto.randomUUID() }),
    onSuccess: async (result) => { setCopyStatus(undefined); setIssued(result); setCreateOpen(false); setName(""); setAttempted(false); await refresh(); },
  });
  const rotate = useMutation({
    mutationFn: () => {
      if (!rotating) throw new Error("Select a token to rotate.");
      return controlPlaneApi.rotateWorkspaceMcpToken(workspaceId, rotating.id, { expiryDays, overlapMinutes, idempotencyKey: crypto.randomUUID() });
    },
    onSuccess: async (result) => { setCopyStatus(undefined); setIssued(result); setRotating(undefined); setOverlapMinutes(5); await refresh(); },
  });
  const revoke = useMutation({
    mutationFn: (tokenId: string) => controlPlaneApi.revokeWorkspaceMcpToken(workspaceId, tokenId, crypto.randomUUID()),
    onSuccess: async () => { setPendingRevoke(undefined); await refresh(); },
  });
  const now = Date.now();
  const rows = filterWorkspaceMcpTokens(tokens.data ?? [], { query, statuses }, now);
  const columns: readonly DataTableColumn<WorkspaceMcpTokenDto>[] = [
    { key: "name", header: "Name", sortable: true, sortValue: (token) => token.name, render: (token) => <strong>{token.name}</strong> },
    { key: "status", header: "Status", sortable: true, sortValue: (token) => getWorkspaceMcpTokenDisplayStatus(token, now), render: (token) => { const status = getWorkspaceMcpTokenDisplayStatus(token, now); return <Badge tone={status === "active" ? "success" : status === "revoked" ? "neutral" : "warn"}>{status}</Badge>; } },
    { key: "prefix", header: "Prefix", render: (token) => <code>{token.credentialPrefix}</code> },
    { key: "expires", header: "Expires", sortable: true, sortValue: (token) => token.expiresAt, render: (token) => formatDateTime(token.expiresAt) },
    { key: "used", header: "Last used", sortable: true, sortValue: (token) => token.lastUsedAt ?? "", render: (token) => token.lastUsedAt ? formatDateTime(token.lastUsedAt) : "Never" },
  ];
  const clearFilters = () => { setQuery(""); setStatuses([]); };
  const closeCreate = () => { if (create.isPending) return; setCreateOpen(false); setName(""); setAttempted(false); create.reset(); };
  const closeRotate = () => { if (rotate.isPending) return; setRotating(undefined); setOverlapMinutes(5); rotate.reset(); };
  return (
    <>
      <PageHeader title="Agent MCP access" subtitle="Issue least-privilege, Workspace-scoped credentials. Plaintext is shown exactly once." actions={<Button onClick={() => setCreateOpen(true)}>New token</Button>} />
      {issued ? (
        <Alert variant="success" title="Copy this credential now" message="It cannot be retrieved again. Store it in AgentOps secrets, then dismiss it." />
      ) : null}
      {issued ? (
        <div className="issued-credential">
          <code>{issued.credential}</code>
          <Button variant="secondary" onClick={() => { void copyToClipboard(issued.credential).then(() => setCopyStatus("copied"), () => setCopyStatus("failed")); }}>{copyStatus === "copied" ? "Copied" : "Copy"}</Button>
          <Button variant="secondary" onClick={() => setIssued(undefined)}>Dismiss</Button>
        </div>
      ) : null}
      {copyStatus === "copied" ? <Alert variant="success" title="Credential copied" message="Store it now in the AgentOps secret manager." /> : null}
      {copyStatus === "failed" ? <Alert variant="error" title="Clipboard unavailable" message="Select and copy the visible credential manually before dismissing it." /> : null}
      <ListFiltersBar search={query} onSearchChange={setQuery} searchLabel="Search agent tokens" searchPlaceholder="Search name or prefix" pills={<Filter label="Status" select="multi" selected={statuses} onSelect={(value) => setStatuses(toggleControlledFilterValue(statuses, value as typeof statuses[number], "multiple"))} options={[{ value: "active", label: "Active" }, { value: "overlap", label: "Overlap" }, { value: "expired", label: "Expired" }, { value: "revoked", label: "Revoked" }]} />} />
      {statuses.length ? <ActiveFiltersRow filters={[{ label: `Status: ${statuses.join(", ")}`, onRemove: () => setStatuses([]) }]} onClearAll={clearFilters} /> : null}
      {revoke.error ? <Alert variant="error" title="Token could not be revoked" message={errorMessage(revoke.error)} /> : null}
      {tokens.error ? <div className="state-panel"><Alert variant="error" title="Agent credentials could not load" message={errorMessage(tokens.error)} /><Button variant="secondary" onClick={() => tokens.refetch()}>Retry</Button></div> : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(token) => token.id}
          loading={tokens.isLoading}
          onClearFilters={clearFilters}
          rowActions={(token) => <Menu align="end" trigger={<IconButton aria-label={`Actions for ${token.name}`} variant="ghost"><Icon name="ellipsis" size={16} /></IconButton>}>
            {token.status === "active" && token.replacementTokenId === null ? <MenuItem onSelect={() => { setExpiryDays(90); setRotating(token); }}>Rotate token</MenuItem> : null}
            {token.status !== "revoked" ? <MenuItem tone="danger" onSelect={() => setPendingRevoke(token)}>Revoke token</MenuItem> : null}
          </Menu>}
        />
      )}
      <FormDialog title="New agent token" description="The credential is scoped to this Workspace and mcp:branding:* only." open={createOpen} onOpenChange={(open) => { if (open) setCreateOpen(true); else closeCreate(); }} onCancel={closeCreate} onSubmit={() => { setAttempted(true); if (name.trim().length >= 2 && !create.isPending) create.mutate(); }} submitLabel="Issue token" submitDisabled={name.trim().length < 2} submitting={create.isPending} error={create.error ? errorMessage(create.error) : undefined}>
        <Field label="Name" required error={attempted && name.trim().length < 2 ? "Enter at least 2 characters." : undefined} state={attempted && name.trim().length < 2 ? "invalid" : "default"}>{(control) => <Input {...fieldControlProps(control)} autoFocus value={name} minLength={2} maxLength={100} required placeholder="e.g. Local coding agent" onChange={(event) => setName(event.target.value)} />}</Field>
        <Field label="Lifetime">{(control) => <InputSelect {...fieldControlProps(control)} value={String(expiryDays)} onValueChange={(value) => setExpiryDays(Number(value))} options={credentialLifetimeOptions} />}</Field>
      </FormDialog>
      <FormDialog title={rotating ? `Rotate ${rotating.name}` : "Rotate token"} description="A replacement token is issued with a bounded overlap so consumers can rotate without downtime." open={Boolean(rotating)} onOpenChange={(open) => { if (!open) closeRotate(); }} onCancel={closeRotate} onSubmit={() => { if (rotating && !rotate.isPending) rotate.mutate(); }} submitLabel="Issue replacement" submitting={rotate.isPending} error={rotate.error ? errorMessage(rotate.error) : undefined}>
        <Field label="Replacement lifetime">{(control) => <InputSelect {...fieldControlProps(control)} value={String(expiryDays)} onValueChange={(value) => setExpiryDays(Number(value))} options={credentialLifetimeOptions} />}</Field>
        <Field label="Overlap minutes" hint="The old credential remains valid only for this bounded migration window.">{(control) => <Input {...fieldControlProps(control)} type="number" min={1} max={30} value={overlapMinutes} onChange={(event) => setOverlapMinutes(Number(event.target.value))} />}</Field>
      </FormDialog>
      <ConfirmDialog open={Boolean(pendingRevoke)} title={pendingRevoke ? `Revoke ${pendingRevoke.name}?` : "Revoke token?"} description="Revocation is immediate. The credential will fail on its next request and cannot be restored." cancelLabel="Keep token active" confirmLabel="Revoke token" confirmDisabled={revoke.isPending} variant="danger" onConfirm={() => { if (pendingRevoke) revoke.mutate(pendingRevoke.id); }} onOpenChange={(open) => { if (!open && !revoke.isPending) setPendingRevoke(undefined); }}>
        {pendingRevoke ? <p className="token-revoke-prefix">Credential prefix: <code>{pendingRevoke.credentialPrefix}</code></p> : null}
      </ConfirmDialog>
    </>
  );
}

function RuntimeAccessPanel({ workspaceId }: { readonly workspaceId: string }) {
  const queryClient = useQueryClient();
  const credentials = useWorkspaceRuntimeCredentials(workspaceId);
  const [query, setQuery] = useState("");
  const [statuses, setStatuses] = useState<readonly ("active" | "expired" | "revoked")[]>([]);
  const [scopes, setScopes] = useState<readonly WorkspaceRuntimeCredentialDto["scopes"][number][]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [consumerId, setConsumerId] = useState("");
  const [expiryDays, setExpiryDays] = useState(90);
  const [scope, setScope] = useState<WorkspaceRuntimeCredentialDto["scopes"][number]>("branding:resolve");
  const [attempted, setAttempted] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState<WorkspaceRuntimeCredentialDto>();
  const [issued, setIssued] = useState<{ plaintext: string; credential: WorkspaceRuntimeCredentialDto }>();
  const [copyStatus, setCopyStatus] = useState<"copied" | "failed">();
  const refresh = () => queryClient.invalidateQueries({ queryKey: brandingQueryKeys.runtimeCredentials(workspaceId) });
  const create = useMutation({
    mutationFn: () => controlPlaneApi.createWorkspaceRuntimeCredential(workspaceId, {
      consumerId: consumerId.trim(),
      scopes: [scope],
      expiryDays,
      idempotencyKey: crypto.randomUUID(),
    }),
    onSuccess: async (result) => { setCopyStatus(undefined); setIssued(result); setCreateOpen(false); setConsumerId(""); setAttempted(false); await refresh(); },
  });
  const revoke = useMutation({
    mutationFn: (credentialId: string) => controlPlaneApi.revokeWorkspaceRuntimeCredential(workspaceId, credentialId, crypto.randomUUID()),
    onSuccess: async () => { setPendingRevoke(undefined); await refresh(); },
  });
  const now = Date.now();
  const rows = filterWorkspaceRuntimeCredentials(credentials.data ?? [], { query, statuses, scopes }, now);
  const columns: readonly DataTableColumn<WorkspaceRuntimeCredentialDto>[] = [
    { key: "consumer", header: "Consumer", sortable: true, sortValue: (credential) => credential.consumerId, render: (credential) => <strong>{credential.consumerId}</strong> },
    { key: "status", header: "Status", sortable: true, sortValue: (credential) => getWorkspaceRuntimeCredentialDisplayStatus(credential, now), render: (credential) => { const status = getWorkspaceRuntimeCredentialDisplayStatus(credential, now); return <Badge tone={status === "active" ? "success" : status === "revoked" ? "neutral" : "warn"}>{status}</Badge>; } },
    { key: "scope", header: "Scope", sortable: true, sortValue: (credential) => credential.scopes.join(" "), render: (credential) => credential.scopes.map((item) => <code key={item}>{item}</code>) },
    { key: "prefix", header: "Prefix", render: (credential) => <code>{credential.credentialPrefix}</code> },
    { key: "expires", header: "Expires", sortable: true, sortValue: (credential) => credential.expiresAt ?? "", render: (credential) => credential.expiresAt ? formatDateTime(credential.expiresAt) : "No expiry" },
    { key: "used", header: "Last used", sortable: true, sortValue: (credential) => credential.lastUsedAt ?? "", render: (credential) => credential.lastUsedAt ? formatDateTime(credential.lastUsedAt) : "Never" },
  ];
  const clearFilters = () => { setQuery(""); setStatuses([]); setScopes([]); };
  const closeCreate = () => { if (create.isPending) return; setCreateOpen(false); setConsumerId(""); setAttempted(false); create.reset(); };
  return (
    <>
      <PageHeader title="Runtime credentials" subtitle="Issue server-only credentials for SSR branding resolution. Plaintext is shown exactly once." actions={<Button onClick={() => setCreateOpen(true)}>New credential</Button>} />
      {issued ? (
        <>
          <Alert variant="success" title="Copy this credential now" message="Store it in the consumer secret manager. It cannot be retrieved again." />
          <div className="issued-credential">
            <code>{issued.plaintext}</code>
            <Button variant="secondary" onClick={() => { void copyToClipboard(issued.plaintext).then(() => setCopyStatus("copied"), () => setCopyStatus("failed")); }}>{copyStatus === "copied" ? "Copied" : "Copy"}</Button>
            <Button variant="secondary" onClick={() => setIssued(undefined)}>Dismiss</Button>
          </div>
        </>
      ) : null}
      {copyStatus === "copied" ? <Alert variant="success" title="Credential copied" message="Store it now in the consumer secret manager." /> : null}
      {copyStatus === "failed" ? <Alert variant="error" title="Clipboard unavailable" message="Select and copy the visible credential manually before dismissing it." /> : null}
      <Alert variant="info" title="Zero-downtime rotation" message="Issue and validate a replacement in the consumer before revoking the prior credential." />
      <ListFiltersBar search={query} onSearchChange={setQuery} searchLabel="Search runtime credentials" searchPlaceholder="Search consumer or prefix" pills={<>
        <Filter label="Status" select="multi" selected={statuses} onSelect={(value) => setStatuses(toggleControlledFilterValue(statuses, value as typeof statuses[number], "multiple"))} options={[{ value: "active", label: "Active" }, { value: "expired", label: "Expired" }, { value: "revoked", label: "Revoked" }]} />
        <Filter label="Scope" select="multi" selected={scopes} onSelect={(value) => setScopes(toggleControlledFilterValue(scopes, value as WorkspaceRuntimeCredentialDto["scopes"][number], "multiple"))} options={[{ value: "branding:resolve", label: "Resolve" }, { value: "branding:fallback:export", label: "Fallback export" }]} />
      </>} />
      {statuses.length || scopes.length ? <ActiveFiltersRow filters={[...(statuses.length ? [{ label: `Status: ${statuses.join(", ")}`, onRemove: () => setStatuses([]) }] : []), ...(scopes.length ? [{ label: `Scope: ${scopes.join(", ")}`, onRemove: () => setScopes([]) }] : [])]} onClearAll={clearFilters} /> : null}
      {revoke.error ? <Alert variant="error" title="Credential could not be revoked" message={errorMessage(revoke.error)} /> : null}
      {credentials.error ? <div className="state-panel"><Alert variant="error" title="Runtime credentials could not load" message={errorMessage(credentials.error)} /><Button variant="secondary" onClick={() => credentials.refetch()}>Retry</Button></div> : <DataTable columns={columns} rows={rows} rowKey={(credential) => credential.id} loading={credentials.isLoading} onClearFilters={clearFilters} rowActions={(credential) => credential.status === "active" ? <Menu align="end" trigger={<IconButton aria-label={`Actions for ${credential.consumerId}`} variant="ghost"><Icon name="ellipsis" size={16} /></IconButton>}><MenuItem tone="danger" onSelect={() => setPendingRevoke(credential)}>Revoke credential</MenuItem></Menu> : null} />}
      <FormDialog title="New runtime credential" description="This credential is for a private server consumer and must never be included in a browser bundle." open={createOpen} onOpenChange={(open) => { if (open) setCreateOpen(true); else closeCreate(); }} onCancel={closeCreate} onSubmit={() => { setAttempted(true); if (consumerId.trim().length >= 2 && !create.isPending) create.mutate(); }} submitLabel="Issue credential" submitDisabled={consumerId.trim().length < 2} submitting={create.isPending} error={create.error ? errorMessage(create.error) : undefined}>
        <Field label="Consumer ID" required error={attempted && consumerId.trim().length < 2 ? "Enter a valid consumer ID." : undefined} state={attempted && consumerId.trim().length < 2 ? "invalid" : "default"}>{(control) => <Input {...fieldControlProps(control)} autoFocus value={consumerId} minLength={2} maxLength={200} pattern="[A-Za-z0-9][A-Za-z0-9._:/-]+" required placeholder="e.g. appointment-app-server" onChange={(event) => setConsumerId(event.target.value)} />}</Field>
        <Field label="Scope">{(control) => <InputSelect {...fieldControlProps(control)} value={scope} onValueChange={(value) => setScope(value as typeof scope)} options={[{ value: "branding:resolve", label: "Branding resolve" }, { value: "branding:fallback:export", label: "Fallback export" }]} />}</Field>
        <Field label="Lifetime">{(control) => <InputSelect {...fieldControlProps(control)} value={String(expiryDays)} onValueChange={(value) => setExpiryDays(Number(value))} options={credentialLifetimeOptions} />}</Field>
      </FormDialog>
      <ConfirmDialog open={Boolean(pendingRevoke)} title={pendingRevoke ? `Revoke ${pendingRevoke.consumerId}?` : "Revoke credential?"} description="Revocation is immediate. Validate a replacement in the consumer first." cancelLabel="Keep credential active" confirmLabel="Revoke credential" confirmDisabled={revoke.isPending} variant="danger" onConfirm={() => { if (pendingRevoke) revoke.mutate(pendingRevoke.id); }} onOpenChange={(open) => { if (!open && !revoke.isPending) setPendingRevoke(undefined); }}>
        {pendingRevoke ? <p className="token-revoke-prefix">Credential prefix: <code>{pendingRevoke.credentialPrefix}</code></p> : null}
      </ConfirmDialog>
    </>
  );
}

function WorkspaceAuditPanel({ events, loading, error, retry, organization = false }: {
  readonly events: readonly AuditEventDto[];
  readonly loading: boolean;
  readonly error: Error | null;
  readonly retry: () => unknown;
  readonly organization?: boolean;
}) {
  const [query, setQuery] = useState("");
  const [decisions, setDecisions] = useState<readonly AuditEventDto["decision"][]>([]);
  const [actorTypes, setActorTypes] = useState<readonly AuditEventDto["actorType"][]>([]);
  const rows = filterAuditEvents(events, { query, decisions, actorTypes });
  const columns: readonly DataTableColumn<AuditEventDto>[] = [
    { key: "action", header: "Action", sortable: true, sortValue: (event) => event.action, render: (event) => <strong>{event.action}</strong> },
    { key: "decision", header: "Decision", sortable: true, sortValue: (event) => event.decision, render: (event) => <Badge tone={event.decision === "completed" || event.decision === "allowed" ? "success" : event.decision === "failed" || event.decision === "denied" ? "danger" : "neutral"}>{event.decision}</Badge> },
    { key: "actor", header: "Actor", sortable: true, sortValue: (event) => `${event.actorType}:${event.actorId}`, render: (event) => <div className="table-primary"><span>{humanizeIdentifier(event.actorType)}</span><code>{event.actorId}</code></div> },
    { key: "resource", header: "Resource", sortable: true, sortValue: (event) => event.resourceType, render: (event) => <div className="table-primary"><span>{humanizeIdentifier(event.resourceType)}</span><code>{event.resourceId}</code></div> },
    ...(organization ? [{ key: "workspace", header: "Workspace", sortable: true, sortValue: (event: AuditEventDto) => event.workspaceId ?? "", render: (event: AuditEventDto) => event.workspaceId ? <code>{event.workspaceId}</code> : "Global" }] : []),
    { key: "source", header: "Source", sortable: true, sortValue: (event) => event.sourceSystem, render: (event) => humanizeIdentifier(event.sourceSystem) },
    { key: "created", header: "Occurred", sortable: true, sortValue: (event) => event.createdAt, render: (event) => formatDateTime(event.createdAt) },
  ];
  const clear = () => { setQuery(""); setDecisions([]); setActorTypes([]); };
  return (
    <>
      <ListFiltersBar search={query} onSearchChange={setQuery} searchLabel="Search audit events" searchPlaceholder="Search action, actor, or resource" trailing={<Badge tone="neutral">Latest {events.length} loaded</Badge>} pills={<>
        <Filter label="Decision" select="multi" selected={decisions} onSelect={(value) => setDecisions(toggleControlledFilterValue(decisions, value as AuditEventDto["decision"], "multiple"))} options={[{ value: "allowed", label: "Allowed" }, { value: "denied", label: "Denied" }, { value: "completed", label: "Completed" }, { value: "failed", label: "Failed" }]} />
        <Filter label="Actor type" select="multi" selected={actorTypes} onSelect={(value) => setActorTypes(toggleControlledFilterValue(actorTypes, value as AuditEventDto["actorType"], "multiple"))} options={[{ value: "human", label: "Human" }, { value: "service_principal", label: "Service principal" }, { value: "workspace_mcp_token", label: "Workspace MCP token" }, { value: "runtime", label: "Runtime" }, { value: "system", label: "System" }]} />
      </>} />
      {decisions.length || actorTypes.length ? <ActiveFiltersRow filters={[...(decisions.length ? [{ label: `Decision: ${decisions.join(", ")}`, onRemove: () => setDecisions([]) }] : []), ...(actorTypes.length ? [{ label: `Actor: ${actorTypes.map(humanizeIdentifier).join(", ")}`, onRemove: () => setActorTypes([]) }] : [])]} onClearAll={clear} /> : null}
      {error ? <div className="state-panel"><Alert variant="error" title="Audit could not load" message={errorMessage(error)} /><Button variant="secondary" onClick={retry}>Retry</Button></div> : <DataTable columns={columns} rows={rows} rowKey={(event) => event.id} loading={loading} onClearFilters={clear} />}
    </>
  );
}

function SystemBrandingsPanel({ templates, loading, error, retry, workspaceId, onCreated, readOnly }: {
  readonly templates: readonly SystemBrandingSummaryDto[];
  readonly loading: boolean;
  readonly error: Error | null;
  readonly retry: () => unknown;
  readonly workspaceId: string | undefined;
  readonly onCreated: (result: unknown) => void | Promise<void>;
  readonly readOnly: boolean;
}) {
  const [query, setQuery] = useState("");
  const [categoriesSelected, setCategoriesSelected] = useState<readonly string[]>([]);
  const [statuses, setStatuses] = useState<readonly SystemBrandingSummaryDto["status"][]>([]);
  const [expandedTemplate, setExpandedTemplate] = useState<string>();
  const [visibleCount, setVisibleCount] = useState(8);
  const categories = useMemo(() => [...new Set(templates.map((template) => template.category))].sort(), [templates]);
  const filteredSummaries = filterSystemBrandingGallery(
    templates.map((template) => createSystemBrandingGalleryRecord(template, undefined)),
    {
      query,
      category: categoriesSelected[0] ?? "all",
      density: "all",
      typography: "all",
    },
  ).filter((record) => !statuses.length || statuses.includes(record.summary.status));
  const visibleSummaries = filteredSummaries.slice(0, visibleCount).map((record) => record.summary);
  const detailQueries = useQueries({
    queries: visibleSummaries.map((template) => ({
      queryKey: brandingQueryKeys.systemBranding(template.id, template.version),
      queryFn: () => controlPlaneApi.getSystemBranding(template.id, template.version),
      staleTime: Number.POSITIVE_INFINITY,
      enabled: !loading && !error,
    })),
  });
  const visibleRecords = visibleSummaries.map((template, index) =>
    createSystemBrandingGalleryRecord(template, detailQueries[index]?.data),
  );
  const clear = () => { setQuery(""); setCategoriesSelected([]); setStatuses([]); setVisibleCount(8); };
  return (
    <>
      {error ? <><Alert variant="error" title="System brandings could not load" message={errorMessage(error)} /><Button variant="secondary" onClick={retry}>Retry</Button></> : loading ? <Spinner aria-label="Loading system brandings" /> : (
        <>
          <ListFiltersBar search={query} onSearchChange={(value) => { setQuery(value); setVisibleCount(8); }} searchLabel="Search System brandings" searchPlaceholder="Search name, tag, or ID" trailing={<Badge tone="neutral">{filteredSummaries.length} matches</Badge>} pills={<>
            <Filter label="Category" select="single" selected={categoriesSelected} onSelect={(value) => { setCategoriesSelected(toggleControlledFilterValue(categoriesSelected, value, "single")); setVisibleCount(8); }} options={categories.map((value) => ({ value, label: value }))} />
            <Filter label="Status" select="multi" selected={statuses} onSelect={(value) => { setStatuses(toggleControlledFilterValue(statuses, value as SystemBrandingSummaryDto["status"], "multiple")); setVisibleCount(8); }} options={[{ value: "available", label: "Available" }, { value: "deprecated", label: "Deprecated" }]} />
          </>} />
          {categoriesSelected.length || statuses.length ? <ActiveFiltersRow filters={[...(categoriesSelected.length ? [{ label: `Category: ${categoriesSelected[0]}`, onRemove: () => { setCategoriesSelected([]); setVisibleCount(8); } }] : []), ...(statuses.length ? [{ label: `Status: ${statuses.join(", ")}`, onRemove: () => { setStatuses([]); setVisibleCount(8); } }] : [])]} onClearAll={clear} /> : null}
          <div className="system-branding-grid">
            {visibleRecords.map(({ summary: template, detail, definition, density: templateDensity, typography: templateTypography }, index) => (
              <Card key={`${template.id}@${template.version}`} role="article">
                <div className="system-branding-card__badges">
                  <Badge tone={template.status === "available" ? "success" : "warn"}>{template.status}</Badge>
                  <Badge tone="neutral">{template.category}</Badge>
                </div>
                <h3>{template.name}</h3>
                <p>{template.description}</p>
                {definition ? (
                  <SystemBrandingLivePreview definition={definition} />
                ) : detailQueries[index]?.isError ? (
                  <Alert variant="error" title="Preview unavailable" message="The exact template definition could not be loaded." />
                ) : (
                  <div className="system-branding-preview-loading"><Spinner aria-label={`Loading ${template.name} preview`} /></div>
                )}
                <div className="system-branding-card__tags">{template.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
                <code>{template.id}@{template.version}</code>
                <small>Schema {template.compatibility.schemaVersion} · compiler ≥ {template.compatibility.minimumCompilerVersion}</small>
                <div className="system-branding-card__actions">
                  <Button
                    variant="secondary"
                    aria-expanded={expandedTemplate === `${template.id}@${template.version}`}
                    onClick={() => setExpandedTemplate((current) => current === `${template.id}@${template.version}` ? undefined : `${template.id}@${template.version}`)}
                  >Details</Button>
                  {!readOnly ? <DraftDialog
                    templates={[template]}
                    initialTemplate={template}
                    initialTitle={`${template.name.slice(0, 110)} draft`}
                    trigger={<Button disabled={!workspaceId || template.status !== "available"}>Use as new draft</Button>}
                    onCreate={(_selected, title) => {
                      if (!workspaceId) throw new Error("Select a workspace before creating a draft.");
                      return controlPlaneApi.createVersion(workspaceId, { title, source: { type: "system_branding", templateId: template.id, templateVersion: template.version }, idempotencyKey: crypto.randomUUID() });
                    }}
                    onCreated={onCreated}
                  /> : <Badge tone="info">Preview only</Badge>}
                </div>
                {expandedTemplate === `${template.id}@${template.version}` ? (
                  <SystemBrandingDetails
                    template={detail}
                    density={templateDensity}
                    typography={templateTypography}
                  />
                ) : null}
              </Card>
            ))}
            {filteredSummaries.length === 0 ? <p>No system brandings match these filters.</p> : null}
          </div>
          {visibleRecords.length < filteredSummaries.length ? (
            <Button variant="secondary" onClick={() => setVisibleCount((count) => count + 8)}>
              Load more System brandings
            </Button>
          ) : null}
        </>
      )}
    </>
  );
}

function SystemBrandingLivePreview({ definition }: { readonly definition: BrandingDefinition }) {
  const [artifact, setArtifact] = useState<CompiledBrandingArtifact>();
  const [compileError, setCompileError] = useState<string>();
  useEffect(() => {
    let active = true;
    setArtifact(undefined);
    setCompileError(undefined);
    void compileBrandingDefinition(definition).then((result) => {
      if (!active) return;
      if (result.ok) setArtifact(result.artifact);
      else setCompileError("The pinned template did not compile.");
    });
    return () => { active = false; };
  }, [definition]);
  if (compileError) return <Alert variant="error" title="Preview unavailable" message={compileError} />;
  if (!artifact) return <div className="system-branding-preview-loading"><Spinner aria-label="Compiling live preview" /></div>;
  const modes = ["light", "dark"].map((colorScheme) =>
    Object.values(artifact.modes).find((mode) => mode.colorScheme === colorScheme),
  ).filter((mode): mode is NonNullable<typeof mode> => Boolean(mode));
  return (
    <div className="system-branding-live-preview">
      <style data-lemn-system-branding-preview>{artifact.fullCss}</style>
      {modes.map((mode) => {
        const compiledMode = getCompiledMode(artifact, mode.modeId);
        return (
          <section
            {...compiledMode.attributes}
            className="system-branding-mode-preview"
            key={compiledMode.modeId}
            aria-label={`${compiledMode.colorScheme} component preview`}
          >
            <span className="system-branding-mode-preview__label">{compiledMode.colorScheme}</span>
            <Card title="Appointment status">
              <p>12 confirmed · 3 pending</p>
              <ProgressBar aria-label="Schedule utilization" value={72} />
              <div className="system-branding-mode-preview__controls">
                <Checkbox checked aria-label="Send reminder" />
                <Badge tone="success">Ready</Badge>
                <Button>Review</Button>
              </div>
            </Card>
          </section>
        );
      })}
    </div>
  );
}

function SystemBrandingDetails({ template, density, typography }: {
  readonly template: SystemBrandingDto | undefined;
  readonly density: string | undefined;
  readonly typography: string | undefined;
}) {
  if (!template) return <div className="system-branding-details"><Spinner aria-label="Loading branding details" /></div>;
  return (
    <dl className="system-branding-details">
      <div><dt>Definition hash</dt><dd><code>{template.definitionHash}</code></dd></div>
      <div><dt>Density</dt><dd>{density ?? "Unavailable"}</dd></div>
      <div><dt>Typography</dt><dd>{typography ?? "Unavailable"}</dd></div>
      <div><dt>Compatibility</dt><dd>Schema {template.compatibility.schemaVersion}; compiler ≥ {template.compatibility.minimumCompilerVersion}</dd></div>
    </dl>
  );
}

function PreviewSessionsPanel({
  workspaceId,
  sessions,
  editableVersions,
  selectedVersion,
  onSelectVersion,
  targets,
  loading,
  error,
  retry,
  hasMore,
  loadingMore,
  loadMore,
  onChanged,
  onNotice,
  initialCreateRequest,
  onInitialCreateRequestConsumed,
}: {
  readonly workspaceId: string;
  readonly sessions: readonly ManagedPreviewSessionDto[];
  readonly editableVersions: readonly BrandingVersionDto[];
  readonly selectedVersion: BrandingVersionDto | undefined;
  readonly onSelectVersion: (versionId: string) => void;
  readonly targets: readonly PreviewTargetDto[];
  readonly loading: boolean;
  readonly error: Error | null;
  readonly retry: () => unknown;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly loadMore: () => unknown;
  readonly onChanged: () => Promise<void>;
  readonly onNotice: (notice: { tone: "success" | "error"; message: string }) => void;
  readonly initialCreateRequest?: {
    readonly key: string;
    readonly draftId: string;
    readonly targetId: string;
    readonly initialModeId: string;
  };
  readonly onInitialCreateRequestConsumed: () => void;
}) {
  const [query, setQuery] = useState("");
  const [statuses, setStatuses] = useState<readonly ManagedPreviewSessionDto["status"][]>([]);
  const [targetIds, setTargetIds] = useState<readonly string[]>([]);
  const [manualCallbackUrl, setManualCallbackUrl] = useState<string>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sourceSession, setSourceSession] = useState<ManagedPreviewSessionDto>();
  const [draftId, setDraftId] = useState(selectedVersion?.id ?? "");
  const [targetId, setTargetId] = useState(targets.find((target) => target.status === "active")?.id ?? "");
  const [initialModeId, setInitialModeId] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [pendingRevoke, setPendingRevoke] = useState<ManagedPreviewSessionDto>();
  const consumedInitialCreateRequest = useRef<string | undefined>(undefined);
  useEffect(() => { if (selectedVersion && !editableVersions.some((version) => version.id === draftId)) setDraftId(selectedVersion.id); }, [draftId, editableVersions, selectedVersion]);
  useEffect(() => {
    if (!initialCreateRequest || consumedInitialCreateRequest.current === initialCreateRequest.key) return;
    consumedInitialCreateRequest.current = initialCreateRequest.key;
    setSourceSession(undefined);
    setDraftId(editableVersions.some((version) => version.id === initialCreateRequest.draftId)
      ? initialCreateRequest.draftId
      : selectedVersion?.id ?? editableVersions[0]?.id ?? "");
    setTargetId(targets.some((target) => target.id === initialCreateRequest.targetId && target.status === "active")
      ? initialCreateRequest.targetId
      : targets.find((target) => target.status === "active")?.id ?? "");
    setInitialModeId(initialCreateRequest.initialModeId);
    setDurationMinutes(30);
    setDialogOpen(true);
    onInitialCreateRequestConsumed();
  }, [editableVersions, initialCreateRequest, onInitialCreateRequestConsumed, selectedVersion?.id, targets]);
  const revoke = useMutation({
    mutationFn: (sessionId: string) => controlPlaneApi.revokePreviewSession(workspaceId, sessionId, crypto.randomUUID()),
    onSuccess: async () => {
      setPendingRevoke(undefined);
      onNotice({ tone: "success", message: "The preview session was revoked." });
      await onChanged();
    },
    onError: (mutationError) => onNotice({ tone: "error", message: errorMessage(mutationError) }),
  });
  const createPreview = useMutation({
    mutationFn: async ({ previewWindow }: { previewWindow: Window | null }) => {
      const version = editableVersions.find((item) => item.id === draftId);
      if (!version) throw new Error("Select an editable draft before creating a preview.");
      if (!targets.some((target) => target.id === targetId && target.status === "active")) throw new Error("Select an active preview target.");
      const handoff = await controlPlaneApi.createPreview(workspaceId, {
        brandingVersionId: version.id,
        expectedDefinitionHash: version.definitionHash,
        targetId,
        ...(initialModeId.trim() ? { initialModeId: initialModeId.trim() } : {}),
        durationMinutes,
        idempotencyKey: crypto.randomUUID(),
      });
      return { handoff, previewWindow };
    },
    onSuccess: async ({ handoff, previewWindow }) => {
      const opened = navigateReservedPreviewWindow(previewWindow, handoff.callbackUrl);
      if (opened) {
        setManualCallbackUrl(undefined);
        onNotice({ tone: "success", message: "The preview opened in a new tab." });
      } else {
        closeReservedPreviewWindow(previewWindow);
        setManualCallbackUrl(handoff.callbackUrl);
        onNotice({ tone: "success", message: "The preview is ready. Open it with the manual link below." });
      }
      setDialogOpen(false);
      setSourceSession(undefined);
      await onChanged();
    },
    onError: (mutationError, variables) => {
      closeReservedPreviewWindow(variables.previewWindow);
      onNotice({ tone: "error", message: errorMessage(mutationError) });
    },
  });
  const openCreate = () => {
    setSourceSession(undefined);
    setDraftId(selectedVersion?.id ?? editableVersions[0]?.id ?? "");
    setTargetId(targets.find((target) => target.status === "active")?.id ?? "");
    setInitialModeId("");
    setDurationMinutes(30);
    createPreview.reset();
    setDialogOpen(true);
  };
  const openUpdated = (session: ManagedPreviewSessionDto) => {
    setSourceSession(session);
    setDraftId(selectedVersion?.id ?? editableVersions[0]?.id ?? "");
    setTargetId(session.targetId);
    setInitialModeId(session.initialModeId ?? "");
    setDurationMinutes(30);
    createPreview.reset();
    setDialogOpen(true);
  };
  const closeDialog = () => { if (createPreview.isPending) return; setDialogOpen(false); setSourceSession(undefined); createPreview.reset(); };
  const rows = filterPreviewSessions(sessions, { query, statuses, targetIds });
  const clear = () => { setQuery(""); setStatuses([]); setTargetIds([]); };
  const columns: readonly DataTableColumn<ManagedPreviewSessionDto>[] = [
    { key: "draft", header: "Draft", sortable: true, sortValue: (session) => session.draftTitle, render: (session) => <div className="table-primary"><strong>{session.draftTitle}</strong><code>{session.definitionHash.slice(0, 12)}</code></div> },
    { key: "status", header: "Status", sortable: true, sortValue: (session) => session.status, render: (session) => <Badge tone={previewSessionTone(session.status)}>{session.status}</Badge> },
    { key: "target", header: "Target", sortable: true, sortValue: (session) => targets.find((target) => target.id === session.targetId)?.name ?? session.targetOrigin, render: (session) => targets.find((target) => target.id === session.targetId)?.name ?? session.targetOrigin },
    { key: "mode", header: "Mode", sortable: true, sortValue: (session) => session.initialModeId ?? "", render: (session) => session.initialModeId ?? "Consumer default" },
    { key: "created", header: "Created", sortable: true, sortValue: (session) => session.createdAt, render: (session) => formatDateTime(session.createdAt) },
    { key: "expiry", header: "Effective expiry", sortable: true, sortValue: (session) => session.effectiveExpiresAt, render: (session) => formatDateTime(session.effectiveExpiresAt) },
  ];
  const canSubmit = Boolean(editableVersions.find((version) => version.id === draftId)) && Boolean(targets.find((target) => target.id === targetId && target.status === "active")) && durationMinutes >= 1 && durationMinutes <= 1440;

  return (
    <>
      <PageHeader title="Preview sessions" subtitle="Inspect, replace, and revoke expiring, target-bound preview handoffs." actions={<div className="preview-header-actions">{editableVersions.length > 0 ? <div className="preview-version-select"><span>Default draft</span><InputSelect aria-label="Default draft for previews" value={selectedVersion?.id ?? editableVersions[0]!.id} onValueChange={onSelectVersion} options={editableVersions.map((version) => ({ value: version.id, label: version.title, hint: version.definitionHash.slice(0, 10) }))} /></div> : null}<Button disabled={!editableVersions.length || !targets.some((target) => target.status === "active")} onClick={openCreate}>New preview</Button></div>} />
      {manualCallbackUrl ? (
        <div className="preview-session-manual-link" role="status">
          <Alert variant="success" title="Preview ready" message="The browser blocked the new tab. Use this one-time link instead." />
          <a href={manualCallbackUrl} target="_blank" rel="noreferrer" onClick={() => setManualCallbackUrl(undefined)}>Open preview manually</a>
        </div>
      ) : null}
      <ListFiltersBar search={query} onSearchChange={setQuery} searchLabel="Search preview sessions" searchPlaceholder="Search draft, target, hash, or mode" trailing={<Badge tone="neutral">{sessions.length} loaded</Badge>} pills={<>
        <Filter label="Status" select="multi" selected={statuses} onSelect={(value) => setStatuses(toggleControlledFilterValue(statuses, value as ManagedPreviewSessionDto["status"], "multiple"))} options={[{ value: "ready", label: "Ready" }, { value: "stale", label: "Stale" }, { value: "expired", label: "Expired" }, { value: "revoked", label: "Revoked" }]} />
        <Filter label="Target" select="multi" selected={targetIds} onSelect={(value) => setTargetIds(toggleControlledFilterValue(targetIds, value, "multiple"))} options={targets.map((target) => ({ value: target.id, label: target.name }))} />
      </>} />
      {statuses.length || targetIds.length ? <ActiveFiltersRow filters={[...(statuses.length ? [{ label: `Status: ${statuses.join(", ")}`, onRemove: () => setStatuses([]) }] : []), ...(targetIds.length ? [{ label: `Targets: ${targetIds.length}`, onRemove: () => setTargetIds([]) }] : [])]} onClearAll={clear} /> : null}
      {error ? <div className="state-panel"><Alert variant="error" title="Preview sessions could not load" message={errorMessage(error)} /><Button variant="secondary" onClick={retry}>Retry</Button></div> : <DataTable columns={columns} rows={rows} rowKey={(session) => session.id} loading={loading} onClearFilters={clear} rowActions={(session) => {
        const target = targets.find((item) => item.id === session.targetId);
        const canCreate = selectedVersion ? canCreateUpdatedPreview(session, selectedVersion) && target?.status === "active" : false;
        return <Menu align="end" trigger={<IconButton aria-label={`Actions for ${session.draftTitle}`} variant="ghost"><Icon name="ellipsis" size={16} /></IconButton>}>
          {canCreate ? <MenuItem onSelect={() => openUpdated(session)}>Create updated preview</MenuItem> : null}
          {canRevokePreviewSession(session) ? <MenuItem tone="danger" onSelect={() => setPendingRevoke(session)}>Revoke preview</MenuItem> : null}
        </Menu>;
      }} />}
      {hasMore ? <Button variant="secondary" disabled={loadingMore} onClick={loadMore}>{loadingMore ? "Loading…" : "Load more sessions"}</Button> : null}
      <FormDialog title={sourceSession ? "Create updated preview" : "New preview"} description={sourceSession ? "Create a replacement handoff from an editable draft while preserving the original target context." : "Create an expiring, target-bound preview handoff."} open={dialogOpen} onOpenChange={(open) => { if (open) setDialogOpen(true); else closeDialog(); }} onCancel={closeDialog} onSubmit={() => { if (!canSubmit || createPreview.isPending) return; setManualCallbackUrl(undefined); createPreview.mutate({ previewWindow: reservePreviewWindow() }); }} submitLabel="Create preview" submitDisabled={!canSubmit} submitting={createPreview.isPending} error={createPreview.error ? errorMessage(createPreview.error) : undefined}>
        <Field label="Branding draft" required>{(control) => <InputSelect {...fieldControlProps(control)} value={draftId} onValueChange={setDraftId} options={editableVersions.map((version) => ({ value: version.id, label: version.title, hint: version.definitionHash.slice(0, 10) }))} />}</Field>
        <Field label="Preview target" required>{(control) => <InputSelect {...fieldControlProps(control)} value={targetId} onValueChange={setTargetId} options={targets.filter((target) => target.status === "active").map((target) => ({ value: target.id, label: target.name, hint: target.origin }))} />}</Field>
        <Field label="Initial mode" hint="Optional. Leave blank to let the consumer choose its default.">{(control) => <Input {...fieldControlProps(control)} value={initialModeId} placeholder="e.g. light" onChange={(event) => setInitialModeId(event.target.value)} />}</Field>
        <Field label="Duration (minutes)" required>{(control) => <Input {...fieldControlProps(control)} type="number" min={1} max={1440} value={durationMinutes} onChange={(event) => setDurationMinutes(Number(event.target.value))} required />}</Field>
      </FormDialog>
      <ConfirmDialog open={Boolean(pendingRevoke)} title={pendingRevoke ? `Revoke preview for ${targets.find((target) => target.id === pendingRevoke.targetId)?.name ?? pendingRevoke.targetOrigin}?` : "Revoke preview?"} description="Revocation is immediate and the existing preview cannot be restored." cancelLabel="Keep preview active" confirmLabel="Revoke preview" confirmDisabled={revoke.isPending} variant="danger" onConfirm={() => { if (pendingRevoke) revoke.mutate(pendingRevoke.id); }} onOpenChange={(open) => { if (!open && !revoke.isPending) setPendingRevoke(undefined); }} />
    </>
  );
}

function PreviewTargetsManager({ workspaceId, targets, loading, error, retry, onSaved }: {
  readonly workspaceId: string;
  readonly targets: readonly PreviewTargetDto[];
  readonly loading: boolean;
  readonly error: Error | null;
  readonly retry: () => unknown;
  readonly onSaved: () => Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const [statuses, setStatuses] = useState<readonly PreviewTargetDto["status"][]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PreviewTargetDto>();
  const [draftTargetId, setDraftTargetId] = useState("");
  const [name, setName] = useState("");
  const [origin, setOrigin] = useState("");
  const [callbackPath, setCallbackPath] = useState("/branding/preview/callback");
  const [audience, setAudience] = useState("");
  const [release, setRelease] = useState("");
  const [status, setStatus] = useState<"active" | "disabled">("active");
  const [attempted, setAttempted] = useState(false);
  const mutation = useMutation({
    mutationFn: () => controlPlaneApi.upsertPreviewTarget(workspaceId, editing?.id ?? draftTargetId, {
      name: name.trim(),
      origin: origin.trim(),
      callbackPath: callbackPath.trim(),
      audience: audience.trim(),
      externalReleaseReference: release.trim() || null,
      status,
      idempotencyKey: crypto.randomUUID(),
    }),
    onSuccess: async () => { setOpen(false); await onSaved(); reset(); },
  });
  const reset = () => {
    setEditing(undefined);
    setDraftTargetId("");
    setName("");
    setOrigin("");
    setCallbackPath("/branding/preview/callback");
    setAudience("");
    setRelease("");
    setStatus("active");
    setAttempted(false);
    mutation.reset();
  };
  const openCreate = () => { reset(); setDraftTargetId(crypto.randomUUID()); setOpen(true); };
  const openEdit = (target: PreviewTargetDto) => {
    reset();
    setEditing(target);
    setName(target.name);
    setOrigin(target.origin);
    setCallbackPath(target.callbackPath);
    setAudience(target.audience);
    setRelease(target.externalReleaseReference ?? "");
    setStatus(target.status);
    setOpen(true);
  };
  const close = () => { if (mutation.isPending) return; setOpen(false); reset(); };
  const valid = name.trim().length > 0 && origin.trim().length > 0 && callbackPath.trim().startsWith("/") && audience.trim().length > 0;
  const rows = filterPreviewTargets(targets, { query, statuses });
  const clear = () => { setQuery(""); setStatuses([]); };
  const columns: readonly DataTableColumn<PreviewTargetDto>[] = [
    { key: "name", header: "Name", sortable: true, sortValue: (target) => target.name, render: (target) => <strong>{target.name}</strong> },
    { key: "status", header: "Status", sortable: true, sortValue: (target) => target.status, render: (target) => <Badge tone={target.status === "active" ? "success" : "neutral"}>{target.status}</Badge> },
    { key: "origin", header: "Origin", sortable: true, sortValue: (target) => target.origin, render: (target) => <code>{target.origin}</code> },
    { key: "audience", header: "Audience", sortable: true, sortValue: (target) => target.audience, render: (target) => <code>{target.audience}</code> },
    { key: "release", header: "Release", sortable: true, sortValue: (target) => target.externalReleaseReference ?? "", render: (target) => target.externalReleaseReference ?? "—" },
    { key: "updated", header: "Updated", sortable: true, sortValue: (target) => target.updatedAt, render: (target) => formatDateTime(target.updatedAt) },
  ];
  return (
    <>
      <PageHeader title="Preview targets" subtitle="Governed consumer origins, callbacks, audiences, and release references." actions={<Button onClick={openCreate}>New target</Button>} />
      <ListFiltersBar search={query} onSearchChange={setQuery} searchLabel="Search preview targets" searchPlaceholder="Search name, origin, or audience" pills={<Filter label="Status" select="multi" selected={statuses} onSelect={(value) => setStatuses(toggleControlledFilterValue(statuses, value as PreviewTargetDto["status"], "multiple"))} options={[{ value: "active", label: "Active" }, { value: "disabled", label: "Disabled" }]} />} />
      {statuses.length ? <ActiveFiltersRow filters={[{ label: `Status: ${statuses.join(", ")}`, onRemove: () => setStatuses([]) }]} onClearAll={clear} /> : null}
      {error ? <div className="state-panel"><Alert variant="error" title="Preview targets could not load" message={errorMessage(error)} /><Button variant="secondary" onClick={retry}>Retry</Button></div> : <DataTable columns={columns} rows={rows} rowKey={(target) => target.id} loading={loading} onClearFilters={clear} rowActions={(target) => <Menu align="end" trigger={<IconButton aria-label={`Actions for ${target.name}`} variant="ghost"><Icon name="ellipsis" size={16} /></IconButton>}><MenuItem onSelect={() => openEdit(target)}>Edit target</MenuItem></Menu>} />}
      <FormDialog title={editing ? `Edit ${editing.name}` : "New preview target"} description="Every preview handoff pins these exact target values." open={open} onOpenChange={(next) => { if (next) setOpen(true); else close(); }} onCancel={close} onSubmit={() => { setAttempted(true); if (valid && !mutation.isPending) mutation.mutate(); }} submitLabel={editing ? "Save target" : "Register target"} submitDisabled={!valid} submitting={mutation.isPending} error={mutation.error ? errorMessage(mutation.error) : undefined}>
        <Field label="Name" required error={attempted && !name.trim() ? "Name is required." : undefined} state={attempted && !name.trim() ? "invalid" : "default"}>{(control) => <Input {...fieldControlProps(control)} autoFocus value={name} placeholder="e.g. Appointment web app" onChange={(event) => setName(event.target.value)} required />}</Field>
        <Field label="Canonical origin" required error={attempted && !origin.trim() ? "Origin is required." : undefined} state={attempted && !origin.trim() ? "invalid" : "default"}>{(control) => <Input {...fieldControlProps(control)} type="url" value={origin} placeholder="https://app.example.com" onChange={(event) => setOrigin(event.target.value)} required />}</Field>
        <Field label="Callback path" required error={attempted && !callbackPath.trim().startsWith("/") ? "Use an absolute path beginning with /." : undefined} state={attempted && !callbackPath.trim().startsWith("/") ? "invalid" : "default"}>{(control) => <Input {...fieldControlProps(control)} value={callbackPath} onChange={(event) => setCallbackPath(event.target.value)} required />}</Field>
        <Field label="Audience" required error={attempted && !audience.trim() ? "Audience is required." : undefined} state={attempted && !audience.trim() ? "invalid" : "default"}>{(control) => <Input {...fieldControlProps(control)} value={audience} placeholder="e.g. appointment-web" onChange={(event) => setAudience(event.target.value)} required />}</Field>
        <Field label="Release reference">{(control) => <Input {...fieldControlProps(control)} value={release} placeholder="e.g. appointment-web@development" onChange={(event) => setRelease(event.target.value)} />}</Field>
        <Field label="Status">{(control) => <InputSelect {...fieldControlProps(control)} value={status} onValueChange={(value) => setStatus(value as "active" | "disabled")} options={[{ value: "active", label: "Active" }, { value: "disabled", label: "Disabled" }]} />}</Field>
      </FormDialog>
    </>
  );
}

function PublishedVersionActions({ workspaceId, version, activeVersionId, activeVersion, publisher, busy, onDraftCreated, run }: {
  readonly workspaceId: string;
  readonly version: BrandingVersionDto;
  readonly activeVersionId: string | null;
  readonly activeVersion: BrandingVersionDto | null;
  readonly publisher: string | null;
  readonly busy: boolean;
  readonly onDraftCreated: (result: unknown) => void | Promise<void>;
  readonly run: (operation: () => Promise<unknown>, onSuccess?: (result: unknown) => void) => void;
}) {
  const currentVersion = activeVersion
    ? `${activeVersion.title}${activeVersion.version ? ` · v${activeVersion.version}` : ""}`
    : "No active version";
  const activate = () => run(() => controlPlaneApi.activateVersion(workspaceId, version.id, {
    expectedActiveVersionId: activeVersionId,
    idempotencyKey: crypto.randomUUID(),
  }));
  return (
    <div className="version-actions">
      {version.state === "published" && activeVersionId !== version.id ? (
        <ConfirmDialog
          cancelLabel="Keep current version"
          confirmDisabled={busy}
          confirmLabel="Activate version"
          description="Confirm the exact immutable artifact before changing the workspace's active branding."
          onConfirm={activate}
          title="Activate this published branding version?"
          trigger={<Button disabled={busy}>Activate version</Button>}
        >
          <dl className="activation-confirmation">
            <div><dt>Current</dt><dd>{currentVersion}</dd></div>
            <div><dt>Current hash</dt><dd>{activeVersion?.compiledHash ?? "No active artifact"}</dd></div>
            <div><dt>Target</dt><dd>{version.title}{version.version ? ` · v${version.version}` : ""}</dd></div>
            <div><dt>Target hash</dt><dd>{version.compiledHash ?? "Unknown hash"}</dd></div>
            <div><dt>Publisher</dt><dd>{publisher ?? "Unavailable — review workspace audit"}</dd></div>
            <div><dt>Published</dt><dd>{version.publishedAt ? new Date(version.publishedAt).toLocaleString() : "unknown"}</dd></div>
            <div><dt>Validation</dt><dd>Passed before immutable publication</dd></div>
          </dl>
          <p className="activation-confirmation__effect">The next SSR request will observe this change.</p>
        </ConfirmDialog>
      ) : null}
      {version.state === "published" ? (
        <VersionDraftDialog
          version={version}
          trigger={<Button disabled={busy} variant="secondary">Create draft from version</Button>}
          onCreate={(title) => controlPlaneApi.createVersion(workspaceId, {
            title,
            source: { type: "branding_version", brandingVersionId: version.id },
            idempotencyKey: crypto.randomUUID(),
          })}
          onCreated={onDraftCreated}
        />
      ) : null}
      {version.state === "published" && activeVersionId === version.id ? <Badge tone="success">Active</Badge> : null}
    </div>
  );
}

function StateBadge({ version }: { readonly version: BrandingVersionDto }) {
  const label = version.archivedAt ? "archived" : version.state;
  const tone = label === "published" ? "success" : label === "publishing" ? "warn" : "neutral";
  return <Badge tone={tone}>{label}</Badge>;
}

function Failure({ title, error, retry }: { readonly title: string; readonly error: Error; readonly retry: () => unknown }) {
  return <main className="state"><div><Alert variant="error" title={title} message={error.message} /><Button onClick={retry}>Retry</Button></div></main>;
}

function preferredVersion(versions: readonly BrandingVersionDto[] | undefined, activeVersionId?: string | null) {
  return versions?.find((version) => version.state === "draft" && version.archivedAt === null)
    ?? versions?.find((version) => version.id === activeVersionId)
    ?? versions?.[0];
}

function createdVersionId(result: unknown): string | undefined {
  if (!result || typeof result !== "object" || !("id" in result)) return undefined;
  return typeof result.id === "string" ? result.id : undefined;
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString();
}

function humanizeIdentifier(value: string): string {
  return value.replaceAll("_", " ").replaceAll("-", " ").replace(/\b\w/g, (character) => character.toUpperCase());
}

function toWorkspaceSlug(value: string): string {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

async function copyToClipboard(value: string): Promise<void> {
  if (!navigator.clipboard) throw new Error("Clipboard API unavailable");
  await navigator.clipboard.writeText(value);
}

function errorMessage(error: unknown): string {
  if (error instanceof ControlPlaneApiError) return `${error.message} (${error.code ?? error.status})`;
  return error instanceof Error ? error.message : "The operation failed.";
}
